import { useEffect, useMemo, useState } from "react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Camera, CheckCircle2, XCircle, Clock } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DAYS_TR = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];

export default function ShootStats() {
  const { workspace } = useWorkspace();
  const [preset, setPreset] = useState("month"); // week | month | custom
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    const today = new Date();
    if (preset === "week") {
      const day = today.getDay(); const diff = (day + 6) % 7;
      const start = new Date(today); start.setDate(today.getDate() - diff);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      setFrom(iso(start)); setTo(iso(end));
    } else if (preset === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFrom(iso(start)); setTo(iso(end));
    }
  }, [preset]);

  useEffect(() => {
    if (!from || !to) return;
    (async () => {
      try {
        const { data } = await api.get("/shoots/stats", { params: { workspace, date_from: from, date_to: to } });
        setData(data);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Yüklenemedi")); }
    })();
  }, [workspace, from, to]);

  const maxDay = useMemo(() => Math.max(1, ...(data?.by_day || []).map((d) => d.count)), [data]);

  const StatCard = ({ label, value, Icon, color, testId }) => (
    <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center`}><Icon size={20} /></div>
      </div>
      <div className="text-3xl font-bold text-slate-900">{value ?? "—"}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );

  return (
    <FinanceLayout>
      <div className="mb-6">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <BarChart3 size={28} className="text-violet-700" /> Çekim İstatistikleri
        </h1>
        <p className="text-sm text-slate-500 mt-2">Seçili tarih aralığında çekim özetiniz. Tamamlanan sayısı SADECE geçmiş tarihli & onaylanmış çekimleri sayar.</p>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {[["week","Bu Hafta"],["month","Bu Ay"],["custom","Özel"]].map(([v,l]) => (
          <button key={v} onClick={() => setPreset(v)} data-testid={`st-preset-${v}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${preset === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="st-from" className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
            <span className="text-slate-400">—</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="st-to" className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Tamamlanan Çekim" value={data?.total_completed} Icon={CheckCircle2} color="bg-emerald-50 text-emerald-600" testId="st-completed" />
        <StatCard label="Onay Bekleyen" value={data?.by_status?.onay_bekleniyor} Icon={Clock} color="bg-amber-50 text-amber-600" testId="st-pending" />
        <StatCard label="Onaylanan" value={data?.by_status?.onaylandi} Icon={CheckCircle2} color="bg-violet-50 text-violet-600" testId="st-approved" />
        <StatCard label="İptal" value={data?.by_status?.iptal} Icon={XCircle} color="bg-red-50 text-red-600" testId="st-cancelled" />
      </div>

      <div className="grid grid-cols-[1fr_360px] gap-6">
        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="st-byday">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Günlere Göre Dağılım</h3>
          {(!data?.by_day || data.by_day.length === 0) ? (
            <p className="text-sm text-slate-400 py-10 text-center">Bu aralıkta çekim yok.</p>
          ) : (
            <div className="space-y-2">
              {data.by_day.map((d) => {
                const dateObj = new Date(d.date);
                const dayLbl = DAYS_TR[(dateObj.getDay() + 6) % 7];
                const pct = (d.count / maxDay) * 100;
                return (
                  <div key={d.date} className="flex items-center gap-3" data-testid={`st-day-${d.date}`}>
                    <div className="w-24 text-xs text-slate-500 whitespace-nowrap">{d.date} · {dayLbl}</div>
                    <div className="flex-1 h-6 bg-slate-50 rounded-md overflow-hidden">
                      <div className="h-full bg-violet-500 flex items-center justify-end pr-2" style={{ width: `${pct}%` }}>
                        <span className="text-[11px] font-semibold text-white">{d.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="st-staff-vs-ext">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Camera size={14} className="text-violet-600" /> Staf vs Dış Çekimci</h3>
          {(() => {
            const total = (data?.staff_count || 0) + (data?.external_count || 0);
            const staffPct = total ? Math.round((data.staff_count / total) * 100) : 0;
            const extPct = 100 - staffPct;
            return (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1"><span className="text-xs text-slate-600">Staf</span><span className="text-xs font-semibold text-slate-900">{data?.staff_count ?? 0}</span></div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-violet-600" style={{ width: `${staffPct}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between mb-1"><span className="text-xs text-slate-600">Dış Çekimci</span><span className="text-xs font-semibold text-slate-900">{data?.external_count ?? 0}</span></div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-slate-500" style={{ width: `${extPct}%` }} /></div>
                </div>
                <div className="pt-3 border-t border-slate-100 text-xs text-slate-500">Toplam: <span className="font-semibold text-slate-900">{total}</span> çekim</div>
              </div>
            );
          })()}
        </div>
      </div>
    </FinanceLayout>
  );
}
